import React, { useState } from 'react'
import { Box } from '@chakra-ui/react'

import EmptyView from '~components/shared/empty-view'
import ClientSearchResult from './client-search-result'
import GradientDivider from '~components/shared/gradient-divider'
import { useSearchPatients } from '~utils/hooks/use-search-patients'
import SharedStack from '~components/shared/wrappers/shared-stack'
import AsyncWrapper from '~components/shared/wrappers/async-wrapper'
import { scrollbarCSS } from '~components/dashboard/action-item-list'
import SharedSearchInput from '~components/shared/shared-search-input'
import {
    ClientPreviewFieldsFragment,
    ClinicFieldsFragment,
    PatientPreviewFieldsFragment,
} from '~graphql/generated/graphql'
import Pagination from '~components/shared/pagination'
import { ShowInactiveSwitch } from '~components/booking/clinicians/actions'
import { useStateWithDebounce } from '~utils/hooks/use-state-with-debounce'
import { OnSelectArgs } from './types'
import PatientSearchResult from './patient-search-result'

interface PatientSearchContentProps {
    clinic: ClinicFieldsFragment
    onSelect: (args: OnSelectArgs) => void
    showReminderPdfAction: boolean
}

export default function PatientSearchContent({
    clinic,
    onSelect,
    showReminderPdfAction,
}: PatientSearchContentProps) {
    const [value, setValue] = useState<string>('')
    const [showInactive, setShowInactive, debouncedShowInactive] =
        useStateWithDebounce<boolean>(false, 500)

    const {
        patientResults,
        loading,
        count,
        isCountCapped,
        page,
        setPage,
        pageSize,
        setPageSize,
    } = useSearchPatients({
        clinic,
        query: value,
        showInactive: debouncedShowInactive,
    })

    function handlePatientSelect(patient: PatientPreviewFieldsFragment) {
        onSelect({ patient, clinic })
        setValue('')
        setShowInactive(false)
    }

    return (
        <>
            <SharedStack row>
                <Box flex={1}>
                    <SharedSearchInput
                        value={value}
                        onChange={e => setValue(e.target.value)}
                        autoFocus
                        w="100%"
                        size="sm"
                        bg="gray.100"
                        color="gray.800"
                        borderRadius="full"
                        _placeholder={{ color: 'gray.400' }}
                        placeholder="Search clients and patients..."
                    />
                </Box>
                <ShowInactiveSwitch
                    showInactive={showInactive}
                    setShowInactive={setShowInactive}
                />
            </SharedStack>
            <AsyncWrapper
                loading={loading}
                empty={patientResults.length === 0}
                emptyComponent={<EmptyPatientsComponent value={value} />}
            >
                <SharedStack
                    my={4}
                    spacing={2}
                    css={scrollbarCSS}
                    divider={<GradientDivider />}
                >
                    {patientResults.map((res, idx) => (
                        <PatientSearchResult
                            key={res?.id || idx}
                            res={res}
                            value={value}
                            onClick={handlePatientSelect}
                            clinic={clinic}
                            showReminderPdfAction={showReminderPdfAction}
                        />
                    ))}
                </SharedStack>
                <Pagination
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    count={count}
                    isCountCapped={isCountCapped}
                    layoutOverrides={{
                        w: 'full',
                        justifyContent: 'space-between',
                        pb: 2,
                    }}
                />
            </AsyncWrapper>
        </>
    )
}

function EmptyPatientsComponent({ value }: { value: string }) {
    return (
        <EmptyView
            px={6}
            header={value ? undefined : 'Start typing to search patients...'}
            infoText={
                value
                    ? 'No results returned. Ensure your filters are properly set and try again.'
                    : 'You can search by client name or patient name.'
            }
        />
    )
}
